import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

// Page-local presentation ownership only. Conversation and composer state stay
// in each mounted session; network updates never participate in selection.
const docks = new Map();
const listeners = new Set();
let owner = null;
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
const snapshot = () => owner;
const exerciseSelector = '.single-exercise-wrapper, .superset-group-wrapper';
const targets = new Map();
let observer = null;
let observerMargin = '';
let visibilityOrder = 0;

function syncObserver() {
  if (typeof IntersectionObserver === 'undefined') return;
  const inset = Math.max(0, document.querySelector('.top-bar')?.getBoundingClientRect().bottom || 0);
  const margin = `-${Math.ceil(inset)}px 0px 0px 0px`;
  if (observer && observerMargin === margin) return;
  observer?.disconnect();
  observerMargin = margin;
  const current = new IntersectionObserver(entries => {
    if (observer !== current) return;
    const arrivals = [];
    entries.forEach(entry => {
      const record = targets.get(entry.target);
      if (!record) return;
      // With threshold 0, native edge contact is the arrival notification;
      // requiring positive area can miss all later movement into the viewport.
      const visible = entry.isIntersecting;
      if (visible && !record.visible) arrivals.push({ ...record, time: entry.time });
      record.visible = visible;
    });
    // One native batch gives simultaneous arrivals a stable DOM-order tie.
    arrivals.sort((a, b) => b.time - a.time || (a.target.compareDocumentPosition(b.target) & 2 ? 1 : -1));
    if (arrivals.length) {
      const winner = arrivals[0];
      docks.get(winner.token).lastVisible = ++visibilityOrder;
      select(winner.token);
    }
  }, { root: null, rootMargin: margin, threshold: 0 });
  observer = current;
  targets.forEach((record, target) => observer.observe(target));
}

function observeExercises(token, shell) {
  const section = shell.closest('.workout-section');
  if (!section) return () => {};
  const sync = () => {
    const next = new Set(section.querySelectorAll(exerciseSelector));
    targets.forEach((record, target) => {
      if (record.token === token && !next.has(target)) { observer?.unobserve(target); targets.delete(target); }
    });
    syncObserver();
    next.forEach(target => {
      if (targets.has(target)) return;
      targets.set(target, { target, token, visible: false });
      observer?.observe(target);
    });
  };
  const interact = event => {
    if (event.target.closest(exerciseSelector)) select(token, true);
  };
  sync();
  const mutations = new MutationObserver(sync);
  mutations.observe(section, { childList: true, subtree: true });
  section.addEventListener('pointerdown', interact, true);
  section.addEventListener('focusin', interact, true);
  const topBar = document.querySelector('.top-bar');
  const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncObserver);
  if (topBar) resize?.observe(topBar);
  topBar?.addEventListener('transitionend', syncObserver);
  window.addEventListener('resize', syncObserver);
  return () => {
    mutations.disconnect(); resize?.disconnect();
    section.removeEventListener('pointerdown', interact, true);
    section.removeEventListener('focusin', interact, true);
    topBar?.removeEventListener('transitionend', syncObserver);
    window.removeEventListener('resize', syncObserver);
    targets.forEach((record, target) => {
      if (record.token === token) { observer?.unobserve(target); targets.delete(target); }
    });
    if (!targets.size) { observer?.disconnect(); observer = null; }
  };
}


function select(token, explicit = false) {
  if (!docks.has(token) || owner === token) return;
  const previous = docks.get(owner);
  if (!explicit && previous?.shell.classList.contains('is-expanded') && (previous.interacting || previous.shell.contains(document.activeElement))) return;
  // Revoke the old interaction surface before React publishes the new one.
  if (previous) {
    previous.shell.setAttribute('inert', '');
    previous.shell.setAttribute('aria-hidden', 'true');
    if (previous.shell.contains(document.activeElement)) document.activeElement.blur();
  }
  owner = token;
  listeners.forEach(listener => listener());
}

export default function useWorkoutCoachDockOwnership(shellRef) {
  const token = useRef(Symbol('workout-coach-owner')).current;
  const active = useSyncExternalStore(subscribe, snapshot, snapshot) === token;
  const activate = useCallback(() => select(token, true), [token]);
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const record = { shell, interacting: false };
    const startInteraction = () => { record.interacting = true; };
    const endInteraction = () => { record.interacting = false; };
    shell.addEventListener('pointerdown', startInteraction, true);
    document.addEventListener('pointerup', endInteraction, true);
    document.addEventListener('pointercancel', endInteraction, true);
    window.addEventListener('blur', endInteraction);
    docks.set(token, record);
    if (!owner) select(token);
    const stopObserving = observeExercises(token, shell);
    return () => {
      stopObserving();
      shell.removeEventListener('pointerdown', startInteraction, true);
      document.removeEventListener('pointerup', endInteraction, true);
      document.removeEventListener('pointercancel', endInteraction, true);
      window.removeEventListener('blur', endInteraction);
      const wasOwner = owner === token;
      docks.delete(token);
      if (wasOwner) {
        owner = null;
        const next = [...docks.keys()].sort((a, b) => (docks.get(b).lastVisible || 0) - (docks.get(a).lastVisible || 0))[0];
        if (next) select(next);
        else listeners.forEach(listener => listener());
      }
    };
  }, [shellRef, token]);
  return { active, activate };
}
